<template>
  <div class="sf-tab-item sf-hover" data-testid="export-item">
    <div class="sf-grid sf-grid-fluid sf-grid-gutter-none">
      <div class="sf-row">
        <div class="sf-col sf-xs-12 sf-sm-4">
          <a
            v-if="succeeded"
            :href="exportItem.url"
            class="sf-a sf-a-primary"
            target="_blank"
          >
            {{ exportItem.title }}
          </a>
          <div v-if="!succeeded">{{ exportItem.title }}</div>
        </div>

        <div class="sf-col sf-xs-12 sf-sm-3 sf-pad-l-1 sf-break-word">
          {{ exportItem.exportedBy }}
        </div>

        <div class="sf-col sf-xs-12 sf-sm-3 sf-pad-l-1">
          <span>{{ timestamp }}</span>
        </div>

        <div class="sf-col sf-xs-12 sf-sm-2 sf-level sf-level-right sf-nowrap">
          <i
            v-if="processing"
            class="fa fa-spinner fa-spin export-spinner"
            data-testid="spinner-processing"
          />
          <i
            v-if="retrying"
            v-tooltip="retryingTooltip"
            class="fa fa-spinner fa-spin export-spinner sf-c-danger"
            data-testid="spinner-retrying"
          />
          <i
            v-if="failed"
            class="fa fa-exclamation-triangle sf-c-danger"
            data-testid="spinner-failed "
          />
          <span class="tw-flex tw-flex-row tw-gap-2">
            <SfButtonIcon
              v-if="succeeded"
              v-tooltip="I18n.t('js.export.move_to_documents')"
              data-testid="export-item-move-to-documents"
              small
              icon="folder"
              @click="moveHandler()"
            />
            <template v-if="succeeded && appLinks.length == 1">
              <SfButtonIcon
                v-if="succeeded"
                v-tooltip="appLinks[0].name"
                :data-testid="`export-item-applink-${appLinks[0].name}`"
                small
                icon="link"
                @click="
                  appLinkHandler(appLinks[0].url, appLinks[0].openInNewTab)
                "
              />
            </template>
            <SfButtonIcon
              v-if="!processing"
              v-tooltip="I18n.t('js.export.regenerate')"
              data-testid="export-item-regenerate"
              small
              icon="arrows-rotate"
              @click="regenerateHandler()"
            />
            <SfButtonIcon
              v-tooltip="I18n.t('confirmation.delete')"
              data-testid="export-item-delete"
              small
              icon="trash"
              class="sf-tab-item sf-block sf-full sf-a"
              @click="deleteHandler()"
            />
            <SfDropdown
              v-if="succeeded && appLinks.length > 1"
              :dropdown-id="dropdownId"
              :is-open="dropdownExpanded"
              trigger-size="small"
              unstyled-trigger
              :drawer-direction="drawerDirections.left"
              @dropdown-clicked="handleToggleDropdown"
              @dropdown-closed="handleCloseDropdown"
            >
              <template #trigger>
                <div
                  class="
                    tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center
                  "
                >
                  <i class="fa fa-ellipsis-v"></i>
                </div>
              </template>

              <template #menu>
                <a
                  v-for="appLink in appLinks"
                  :key="appLink.name"
                  class="sf-tab-item sf-block sf-full sf-a"
                  @click.stop.prevent="
                    appLinkHandler(appLink.url, appLink.openInNewTab)
                  "
                >
                  <i class="fa fa-link sf-pad-r-1"></i>
                  {{ appLink.name }}
                </a>
              </template>
            </SfDropdown>
          </span>
        </div>
      </div>

      <div v-if="hasMessages" class="div">
        <div class="sf-below-2"></div>
        <div class="sf-row">
          <div class="sf-col sf-xs-12">
            <div
              :class="{
                'sf-alert-danger': failed || retrying,
                'sf-alert-warning': succeeded,
              }"
              class="sf-alert sf-pad-2"
            >
              <p
                v-for="(errorMessage, index) in exportItem.errorMessages"
                :key="`${errorMessage}-${index}`"
                v-dompurify-html="errorMessage"
                :class="{
                  'sf-below-0': index == exportItem.errorMessages.length - 1,
                }"
              ></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AppLink, ExportType } from '@app/application/export/types'

import { computed } from 'vue'

import api from '../api/export-item'
import { dropdowns } from '@app/shared/vue/store/modules/dropdown/constants'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import formatTimestamp from '@app/utils/time-formatter'
import { SfButtonIcon } from '@app/shared/vue/components/sf-button'
import { drawerDirections } from '@app/shared/vue/components/sf-dropdown/constants'

// eslint-disable-next-line no-undef
defineOptions({
  name: 'ExportItem',
})

interface Props {
  dropdownExpanded: boolean
  exportItem: ExportType
  appLinks: AppLink[]
}

const I18n = window.I18n
const dropdownId = dropdowns.EXPORT
const retryingTooltip = I18n.t('js.export.retrying')

const props = defineProps<Props>()

const emit =
  defineEmits<{
    'close-dropdown': []
    'toggle-dropdown': [number]
  }>()

const failed = computed(() => props.exportItem.status === 'failed')
const hasMessages = computed(
  () =>
    props.exportItem.errorMessages.length > 0 &&
    !['started', 'processing'].includes(props.exportItem.status)
)
const processing = computed(
  () =>
    ['started', 'processing'].includes(props.exportItem.status) &&
    !hasMessages.value
)
const retrying = computed(() => props.exportItem.status === 'retrying')
const succeeded = computed(() => props.exportItem.status === 'succeeded')
const timestamp = computed(() =>
  formatTimestamp(props.exportItem.createdAt, {
    shouldUseLocalTimezone: true,
    shouldUseRelativeFormatOnCurrentDate: false,
  })
)

function moveHandler() {
  return api.moveExport(props.exportItem.id)
}

function appLinkHandler(url: string, openInNewTab: boolean) {
  const fullUrl =
    url +
    '&object_id=' +
    props.exportItem.id +
    '&period_id=' +
    props.exportItem.ledgerId
  if (openInNewTab) {
    window.open(fullUrl, '_blank')?.focus()
  } else {
    window.Turbolinks.visit(fullUrl)
  }
}

function regenerateHandler() {
  window.Turbolinks.visit(props.exportItem.links.copy)
}

function deleteHandler() {
  window.sfRemoveConfirm({
    name: props.exportItem.title,
    onConfirm: () => {
      api.deleteExport(props.exportItem.id)
    },
    keep: false,
  })
}

function handleCloseDropdown() {
  emit('close-dropdown')
}

function handleToggleDropdown() {
  emit('toggle-dropdown', props.exportItem.id)
}
</script>
